/*
 * FichajesMapa — visualiza la ruta GPS de un operario en un día.
 *
 * Admin/gestor puede elegir usuario y fecha; hunter ve solo la suya.
 * Muestra:
 *  - Selector de operario + fecha
 *  - Mapa Leaflet con puntos numerados + polilínea de la ruta
 *  - Panel con lista de fichajes ordenados (hora + tipo + coords + site vinculado)
 *  - KPIs: total puntos, km recorridos
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Loader2, MapPin, Calendar, Users, Route } from 'lucide-react';

const KIND_META = {
  entrada_trabajo:   { label: 'Entrada trabajo',    color: '#A3FF12' },
  salida_trabajo:    { label: 'Salida trabajo',     color: '#f87171' },
  visita_site_in:    { label: 'Entrada al site',    color: '#38bdf8' },
  visita_site_out:   { label: 'Salida del site',    color: '#818cf8' },
  descanso_in:       { label: 'Inicio descanso',    color: '#fbbf24' },
  descanso_out:      { label: 'Fin descanso',       color: '#fbbf24' },
};

const AutoFit = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 15); return; }
    const bounds = points.map((p) => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
};

const FichajesMapa = () => {
  const { user } = useAuth();
  const isManager = user && (user.role === 'admin' || user.role === 'gestor');
  const today = new Date().toISOString().slice(0, 10);

  const [day, setDay] = useState(today);
  const [userId, setUserId] = useState(user?.id || '');
  const [users, setUsers] = useState([]);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isManager) return;
    api.get('/admin/users').then(({ data }) => setUsers(data || [])).catch(() => {});
  }, [isManager]);

  useEffect(() => {
    if (!day) return;
    setLoading(true);
    api.get('/time-entries/route', { params: { day, user_id: userId || undefined } })
      .then(({ data }) => setRoute(data))
      .catch(() => setRoute(null))
      .finally(() => setLoading(false));
  }, [day, userId]);

  const points = route?.points || [];
  const polyline = useMemo(() => points.map((p) => [p.lat, p.lng]).filter(([a, b]) => a && b), [points]);
  const center = points[0] ? [points[0].lat, points[0].lng] : [40.4168, -3.7038];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="fichajes-mapa-page">
      <header className="mb-4">
        <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">FICHAJES · MAPA</div>
        <h1 className="text-2xl sm:text-3xl font-black text-white mt-1 inline-flex items-center gap-2">
          <Route className="text-[#A3FF12]"/> Ruta GPS del día
        </h1>
        <p className="text-white/50 text-sm mt-1">Traza la ruta de cada operario para revisar visitas y tiempos.</p>
      </header>

      {/* Filtros */}
      <div className="flex items-end gap-3 flex-wrap mb-4">
        <label className="block">
          <span className="text-[10px] uppercase text-white/50 font-bold flex items-center gap-1"><Calendar size={11}/> Día</span>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} max={today}
            data-testid="fichajes-day-input"
            className="mt-1 bg-black border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
        </label>
        {isManager && (
          <label className="block">
            <span className="text-[10px] uppercase text-white/50 font-bold flex items-center gap-1"><Users size={11}/> Operario</span>
            <select value={userId} onChange={(e) => setUserId(e.target.value)}
              data-testid="fichajes-user-select"
              className="mt-1 bg-black border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-[#A3FF12]/50 min-w-[220px]">
              <option value={user.id}>{user.name} (yo)</option>
              {users.filter((u) => u.id !== user.id).map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* KPIs */}
      {route && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Kpi label="Puntos" value={route.total_points} data-testid="kpi-points"/>
          <Kpi label="Distancia" value={`${route.distance_km} km`} data-testid="kpi-distance"/>
          <Kpi label="Operario" value={route.user?.name || '—'} data-testid="kpi-user"/>
        </div>
      )}

      {/* Mapa */}
      <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/40" style={{ height: 480 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[#A3FF12]" size={24}/></div>
        ) : points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-white/40 text-sm">
            Sin fichajes con GPS en este día.
          </div>
        ) : (
          <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer attribution='© OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
            {polyline.length >= 2 && (
              <Polyline positions={polyline} pathOptions={{ color: '#A3FF12', weight: 3, opacity: 0.85 }}/>
            )}
            {points.map((p, i) => {
              const meta = KIND_META[p.kind] || { label: p.kind, color: '#94a3b8' };
              return (
                <CircleMarker key={p.id || i} center={[p.lat, p.lng]} radius={9}
                  pathOptions={{ color: meta.color, weight: 2, fillColor: '#000', fillOpacity: 0.9 }}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{i + 1}. {meta.label}</div>
                      <div className="text-gray-600">{new Date(p.server_time || p.client_time).toLocaleTimeString('es-ES')}</div>
                      {p.site_id && <div className="text-blue-600 mt-1">Site {p.site_id.slice(0, 8)}</div>}
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
            <AutoFit points={points}/>
          </MapContainer>
        )}
      </div>

      {/* Timeline */}
      {points.length > 0 && (
        <div className="mt-4 bg-black/30 border border-white/10 rounded-2xl overflow-hidden" data-testid="fichajes-timeline">
          <div className="px-4 py-2 border-b border-white/10 text-[10px] font-black tracking-widest text-[#A3FF12]">
            Secuencia del día
          </div>
          <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
            {points.map((p, i) => {
              const meta = KIND_META[p.kind] || { label: p.kind, color: '#94a3b8' };
              const time = new Date(p.server_time || p.client_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
              return (
                <div key={p.id || i} className="px-4 py-2 flex items-center gap-3 text-sm">
                  <span className="text-white/30 font-mono w-6">{i + 1}</span>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }}/>
                  <span className="text-white/80 w-16 font-mono">{time}</span>
                  <span className="text-white flex-1 truncate">{meta.label}</span>
                  <span className="text-white/40 text-[11px]"><MapPin size={10} className="inline"/> {p.lat?.toFixed(4)}, {p.lng?.toFixed(4)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, ...rest }) => (
  <div className="bg-black/30 border border-white/10 rounded-xl p-3" {...rest}>
    <div className="text-[10px] font-black uppercase tracking-widest text-white/40">{label}</div>
    <div className="text-lg font-black text-white mt-1 truncate">{value}</div>
  </div>
);

export default FichajesMapa;
